import { GoogleGenerativeAI } from '@google/generative-ai';

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY || '';

// Initialize Gemini AI only if API key is available
const genAI = API_KEY ? new GoogleGenerativeAI(API_KEY) : null;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const generateTasks = async (prompt: string, retries = 3): Promise<string[]> => {
  if (!API_KEY || !genAI) {
    throw new Error('Gemini API key not found. Please set REACT_APP_GEMINI_API_KEY in your environment variables.');
  }

  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

    const fullPrompt = `Generate 3-5 specific, actionable todo tasks based on this request: "${prompt}". 
    Return only the task titles, one per line, without numbering or bullet points. 
    Make them practical and achievable.`;

    const result = await model.generateContent(fullPrompt);
    const response = await result.response;
    const text = response.text();

    // Split by lines and clean up
    const tasks = text
      .split('\n')
      .map((task: string) => task.trim())
      .filter((task: string) => task.length > 0 && !task.match(/^\d+\./))
      .slice(0, 5); // Limit to 5 tasks

    return tasks;
  } catch (error) {
    if (retries > 0) {
      console.log(`Gemini API failed, retrying... (${retries} attempts left)`);
      await delay(1000); // Wait 1 second before retrying
      return generateTasks(prompt, retries - 1);
    }
    console.error('Error generating tasks with Gemini:', error);
    throw new Error('Failed to generate tasks. Please try again.');
  }
};

export const generateTaskSuggestions = async (context: string): Promise<string[]> => {
  if (!API_KEY || !genAI) {
    throw new Error('Gemini API key not found. Please set REACT_APP_GEMINI_API_KEY in your environment variables.');
  }

  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

    const fullPrompt = `Based on this context: "${context}", suggest 3 relevant todo tasks that would be helpful. 
    Return only the task titles, one per line, without numbering or bullet points.`;

    const result = await model.generateContent(fullPrompt);
    const response = await result.response;
    const text = response.text();

    const tasks = text
      .split('\n')
      .map((task: string) => task.trim())
      .filter((task: string) => task.length > 0 && !task.match(/^\d+\./))
      .slice(0, 3);

    return tasks;
  } catch (error) {
    console.error('Error generating task suggestions:', error);
    throw new Error('Failed to generate suggestions. Please try again.');
  }
};
